import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import EmailButton from './EmailButtonExtension';
import SignatureBlock from './SignatureBlockExtension';
import { toEditorSafeHtml } from '../lib/editorSafeHtml';

/**
 * The composer editor's extension list — the SCHEMA, in one place.
 *
 * Extracted from `RichTextEditor`'s inline `useEditor({ extensions: [...] })`
 * for #597. `lib/editorSafeHtml.js` promises that its output is a fixed point of
 * this schema's `setContent` → `getHTML()` round-trip, and the only honest way
 * to test that promise is against the schema the composer really uses. Building
 * a second extension list inside the test would let the two drift, and a drifted
 * copy would assert the fixed point against markup nothing renders.
 *
 * `RichTextViewer` (the read-only viewer at the bottom of RichTextEditor.jsx) is
 * deliberately NOT built from this: it has no Placeholder, no EmailButton, and
 * `openOnClick: true`. It renders published content and is not a composer.
 *
 * Changing anything here can break the fixed point — the Link/Image
 * `HTMLAttributes` below are re-emitted verbatim by `toEditorSafeHtml`, and the
 * node/mark set decides what it is allowed to keep. `editorSafeHtml.test.js`
 * fails loudly if this drifts, which is the point.
 */
export function buildComposerExtensions({ placeholder = '' } = {}) {
    return [
        StarterKit.configure({
            heading: {
                levels: [1, 2, 3],
            },
        }),
        Underline,
        Link.configure({
            openOnClick: false,
            HTMLAttributes: {
                class: 'text-stone-700 underline hover:text-stone-800',
            },
        }),
        Image.configure({
            HTMLAttributes: {
                class: 'max-w-full h-auto rounded-lg my-4',
            },
        }),
        TextAlign.configure({
            types: ['heading', 'paragraph'],
        }),
        Placeholder.configure({
            placeholder,
        }),
        EmailButton,
        // #970 — the signature as one locked block that keeps its layout.
        // Parsed only from div[data-ncrm-signature], so no other content changes.
        SignatureBlock,
    ];
}

/**
 * The composer editor's `editorProps`, beside the schema for the same reason:
 * so a test can assert the REAL object the editor is constructed with instead
 * of trusting that the source says what it looks like it says.
 *
 * `transformPastedHTML` (#623) reuses `toEditorSafeHtml` rather than defining a
 * second sanitisation policy. It allowlists exactly what this schema can hold,
 * drops <script>/<style> and inline styles, and refuses `javascript:` / `data:`
 * hrefs — the real risk, since a pasted link ends up in a message that is
 * actually sent. It also makes a paste deterministic: the result is a fixed
 * point of setContent → getHTML, so pasting cannot desync the
 * replace-not-stack bookkeeping in TemplateSignatureSelectors.
 *
 * Do NOT swap in `inboxUtils.sanitizeEmailHtml`: it PRESERVES tables and inline
 * styles (it renders received mail in an iframe) and prepends an iframe <style>
 * block, which would land in the composer body.
 */
export function buildComposerEditorProps() {
    return {
        attributes: {
            class: 'prose prose-sm max-w-none focus:outline-none',
        },
        transformPastedHTML: (html) => toEditorSafeHtml(html),
    };
}
